// ═══════════════════════════════════════════════════════════════
// BOUNTIFUL SEA - AI LOGIC
// ═══════════════════════════════════════════════════════════════

import {
  GameState,
  Player,
  PlayerId,
  AIDecision,
  AIConfig,
} from '../../types/bountifulSea';
import { AI_PERSONALITIES } from '../../data/bountifulSea/gameConstants';
import {
  getExpectedRoll,
  getMaxPossibleRoll,
  calculateSuspicionScore,
} from './diceUtils';
import {
  getMinDeclarationForPlayer,
  mustPlayerSinkOrLie,
} from './gameLogic';

// ─────────────────────────────────────────────────────────────────
// MAIN AI DECISION FUNCTION
// ─────────────────────────────────────────────────────────────────

export function makeAIDecision(
  state: GameState,
  playerId: PlayerId
): AIDecision | null {
  const player = state.players[playerId];
  if (!player || !player.isAI || !player.aiPersonality) return null;

  const config = AI_PERSONALITIES[player.aiPersonality];

  switch (state.voyageSubPhase) {
    case 'declaring':
      return makeDeclarationDecision(state, player, config);

    case 'decision':
      return makeSailOrPortDecision(state, player, config);

    case 'awaitingCalls':
      return makeCallDecision(state, player, config);

    default:
      return null;
  }
}

// ─────────────────────────────────────────────────────────────────
// DECLARATION DECISION
// ─────────────────────────────────────────────────────────────────

function makeDeclarationDecision(
  state: GameState,
  player: Player,
  config: AIConfig
): AIDecision {
  if (!player.currentVoyage) {
    return { action: 'declare', value: 1, confidence: 0.5 };
  }

  const voyage = player.currentVoyage;
  const actualTotal = voyage.actualTotal;
  const minRequired = getMinDeclarationForPlayer(player);

  // Check if we MUST lie or sink
  const mustLieOrSink = mustPlayerSinkOrLie(player);

  if (mustLieOrSink) {
    // Decide: lie or sink?
    const shouldLie = Math.random() < config.riskTolerance;

    if (!shouldLie) {
      return {
        action: 'sink',
        confidence: 1 - config.riskTolerance,
        reasoning: 'Cannot beat previous declaration, choosing to sink',
      };
    }

    // Must lie - calculate a believable bluff
    const bluffAmount = Math.ceil(minRequired * (1 + (Math.random() * 0.2)));
    const maxPossible = getMaxPossibleRoll(player.scoundrelDice);

    return {
      action: 'declare',
      value: Math.min(bluffAmount, maxPossible),
      confidence: config.riskTolerance * 0.7,
      reasoning: 'Forced to lie to continue voyage',
    };
  }

  // We CAN declare truthfully - should we lie?
  const shouldLie = Math.random() < config.lieThreshold;

  if (shouldLie) {
    // Calculate bluff amount based on personality
    const bluffedTotal = Math.ceil(actualTotal * config.bluffMultiplier);
    const maxPossible = getMaxPossibleRoll(player.scoundrelDice);

    // Add some randomness to the bluff
    const randomness = 1 + (Math.random() - 0.5) * 0.2;
    const finalBluff = Math.min(
      Math.ceil(bluffedTotal * randomness),
      maxPossible
    );

    // Make sure bluff is at least minimum required
    const declaration = Math.max(finalBluff, minRequired);

    return {
      action: 'declare',
      value: declaration,
      confidence: config.riskTolerance * 0.8,
      reasoning: `Bluffing: actual ${actualTotal}, declaring ${declaration}`,
    };
  }

  // Declare truthfully
  return {
    action: 'declare',
    value: actualTotal,
    confidence: 1.0,
    reasoning: 'Declaring truthfully',
  };
}

// ─────────────────────────────────────────────────────────────────
// SAIL ON vs MAKE PORT DECISION
// ─────────────────────────────────────────────────────────────────

function makeSailOrPortDecision(
  state: GameState,
  player: Player,
  config: AIConfig
): AIDecision {
  if (!player.currentVoyage) {
    return { action: 'makePort', confidence: 1.0 };
  }

  const voyage = player.currentVoyage;
  const currentHaul = voyage.haul;
  const potRemaining = state.bountifulSea;
  const totalPot = currentHaul + potRemaining;

  // Calculate what portion of the pot we've claimed
  const haulRatio = currentHaul / (totalPot || 1);

  // Factor 1: Have we met our haul threshold?
  const metThreshold = haulRatio >= config.portThreshold;

  // Factor 2: How risky is sailing on?
  const minNextDeclaration = getMinDeclarationForPlayer(player);
  const maxPossibleRoll = getMaxPossibleRoll(player.scoundrelDice);
  const expectedRoll = getExpectedRoll(player.scoundrelDice);

  // Risk of not being able to beat our last declaration
  const riskOfFailure =
    minNextDeclaration > expectedRoll
      ? (minNextDeclaration - expectedRoll) / (maxPossibleRoll - expectedRoll)
      : 0;

  // Factor 3: How many dice do we have left?
  const diceRisk = player.scoundrelDice <= 1 ? 0.3 : 0;

  // Factor 4: Is the pot almost empty?
  const potEmptyRisk = potRemaining < 5 ? 0.2 : 0;

  // Calculate overall risk
  const totalRisk = riskOfFailure + diceRisk + potEmptyRisk;

  // Decision
  if (metThreshold || totalRisk > config.riskTolerance) {
    return {
      action: 'makePort',
      confidence: Math.min(1, haulRatio + totalRisk),
      reasoning: `Securing haul of ${currentHaul} coins`,
    };
  }

  // Add some randomness for unpredictability
  if (Math.random() < 0.1) {
    return {
      action: 'makePort',
      confidence: 0.6,
      reasoning: 'Random caution',
    };
  }

  return {
    action: 'sailOn',
    confidence: 1 - totalRisk,
    reasoning: `Sailing on with ${currentHaul} coins, seeking more`,
  };
}

// ─────────────────────────────────────────────────────────────────
// SCOUNDREL CALL DECISION
// ─────────────────────────────────────────────────────────────────

function makeCallDecision(
  state: GameState,
  player: Player,
  config: AIConfig
): AIDecision {
  const target = state.players[state.round.currentPlayerId];
  if (!target || !target.currentVoyage) {
    return { action: 'pass', confidence: 1.0 };
  }

  const voyage = target.currentVoyage;
  const declaredTotal = voyage.declaredTotal;
  if (declaredTotal === null) {
    return { action: 'pass', confidence: 1.0 };
  }

  // Calculate suspicion based on the declaration
  const suspicion = calculateSuspicionScore(
    declaredTotal,
    target.scoundrelDice,
    voyage.previousDeclarations.slice(0, -1) // Exclude current declaration
  );

  // Adjust suspicion based on game context
  let adjustedSuspicion = suspicion;

  // More suspicious if target has been caught lying before
  // (This would require tracking - for now, use base suspicion)

  // More suspicious if target is under pressure (few dice left)
  if (target.scoundrelDice <= 1) {
    adjustedSuspicion *= 0.8; // Less likely to lie when desperate (might sink instead)
  }

  // Less suspicious if it's their first roll of the voyage
  if (voyage.rollCount === 1) {
    adjustedSuspicion *= 0.9;
  }

  // Add personality-based randomness
  const randomFactor = (Math.random() - 0.5) * 0.2;
  adjustedSuspicion += randomFactor;
  adjustedSuspicion = Math.max(0, Math.min(1, adjustedSuspicion));

  // Decision based on call threshold
  if (adjustedSuspicion >= config.callThreshold) {
    // Additional check: do we have dice to risk?
    if (player.scoundrelDice <= 1 && config.riskTolerance < 0.5) {
      return {
        action: 'pass',
        confidence: 0.7,
        reasoning: 'Suspicious but too risky with one die left',
      };
    }

    return {
      action: 'callScoundrel',
      confidence: adjustedSuspicion,
      reasoning: `Suspicion level ${(adjustedSuspicion * 100).toFixed(0)}% exceeds threshold`,
    };
  }

  return {
    action: 'pass',
    confidence: 1 - adjustedSuspicion,
    reasoning: `Suspicion level ${(adjustedSuspicion * 100).toFixed(0)}% below threshold`,
  };
}

// ─────────────────────────────────────────────────────────────────
// AI TIMING & EXECUTION
// ─────────────────────────────────────────────────────────────────

export function getAIActionDelay(personality: string): number {
  // Different personalities "think" at different speeds
  switch (personality) {
    case 'cautious':
      return 2000 + Math.random() * 1000; // Careful consideration
    case 'balanced':
      return 1500 + Math.random() * 500;
    case 'reckless':
      return 800 + Math.random() * 400; // Quick decisions
    default:
      return 1500;
  }
}
